import {getApi} from '@/api/utils';
import {AppButton} from '@/components/AppButton';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {useState} from 'react';
import {useNotify, useRecordContext, useRefresh} from 'react-admin';
import {useNavigate} from 'react-router-dom';

export const ToggleAdminButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const navigate = useNavigate();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isAdmin = record?.is_admin ?? false;

    const handleOpen = () => {
        setOpen(true);
        setError(null);
    };

    const handleClose = () => {
        if (!loading) {
            setOpen(false);
        }
    };

    const handleSubmit = async () => {
        if (!record?.id) return;
        setError(null);
        setLoading(true);

        try {
            const api = getApi(navigate);
            await api.setUserAdminStatus(record.id as string, !isAdmin);
            notify(
                `User ${!isAdmin ? 'promoted to' : 'removed from'} admin successfully`,
                {type: 'success'}
            );
            setOpen(false);
            refresh();
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                `Failed to ${isAdmin ? 'remove' : 'grant'} admin status`;
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    if (!record) return null;

    return (
        <>
            <AppButton
                variant={isAdmin ? 'destructive' : 'secondary'}
                size="small"
                startIcon={
                    isAdmin ? <PersonRemoveIcon /> : <AdminPanelSettingsIcon />
                }
                onClick={handleOpen}
            >
                {isAdmin ? 'Remove Admin' : 'Make Admin'}
            </AppButton>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isAdmin ? 'Remove Admin Access' : 'Grant Admin Access'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}
                    <DialogContentText>
                        {isAdmin ? (
                            <>
                                Are you sure you want to remove admin access for{' '}
                                <strong>
                                    {record.first_name} {record.last_name}
                                </strong>
                                ? They will lose access to the admin dashboard.
                            </>
                        ) : (
                            <>
                                Are you sure you want to grant admin access to{' '}
                                <strong>
                                    {record.first_name} {record.last_name}
                                </strong>
                                ? They will gain access to the admin dashboard.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </AppButton>
                    <AppButton
                        variant={isAdmin ? 'destructive' : 'primary'}
                        onClick={handleSubmit}
                        loading={loading}
                    >
                        {isAdmin ? 'Remove Admin' : 'Grant Admin'}
                    </AppButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
